import React, { memo } from "react"
import { Flex, Button, Select, Drawer } from "antd"
import { FilterIcon } from "ui/Icon/DSIcon/Filter"
import styles from "../index.module.css"
import { ExperienceListingIcon } from "ui/Icon/DSIcon/ExperienceListing"
import { ExperienceTableIcon } from "ui/Icon/DSIcon/ExperienceTable"
import { usePagination } from "../hooks/usePagination"
import PropTypes from "prop-types"
import { useMediaQuery } from "react-responsive"
import { CloseIcon } from "icons"

const FilterButtons = memo(
  ({
    selectedListing,
    setSelectedListing,
    t,
    activeTab,
    onTabChange,
    activeListingTab,
    onListingTabChange,
    publishedCount,
    draftCount,
    unpublishedCount,
    isFilterDropdownOpen,
    onFilterIconClick,
    onCloseFilterDropdown,
    FilterDropdown
  }) => {
    const isCompactDevice = useMediaQuery({ query: "(max-width: 1280px)" })
    const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
    const { setPage } = usePagination(1, 10)

    const experienceTypeOptions = [
      { value: "all", label: t("experiences.all") },
      { value: "private", label: t("experiences.private") },
      { value: "ticketed", label: t("experiences.ticketed") },
      // { value: "rentals", label: t("experiences.rentals") }
    ]

    const listingStatusOptions = [
      { value: "published", label: `${t("experiences.published")} (${publishedCount})` },
      { value: "draft", label: `${t("experiences.drafts")} (${draftCount})` },
      { value: "unpublished", label: `${t("experiences.deactivated")} (${unpublishedCount})` }
    ]

    const onListingChange = (tab) => {
      setSelectedListing(tab)
      setPage(1)
    }

    return (
      <>
        <Flex align="center" className={styles.topFilterTabs}>
          {isCompactDevice ? (
            <Select value={activeTab} options={experienceTypeOptions} onChange={onTabChange} className={styles.mobileTypeDropdown} />
          ) : (
            <>
              <Button
                type="text"
                className={`${styles.tabButton} ${activeTab === "all" ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => onTabChange("all")}
              >
                {t("experiences.all")}
              </Button>
              <Button
                type="text"
                className={`${styles.tabButton} ${activeTab === "private" ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => onTabChange("private")}
              >
                {t("experiences.private")}
              </Button>
              <Button
                type="text"
                className={`${styles.tabButton} ${activeTab === "ticketed" ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => onTabChange("ticketed")}
              >
                {t("experiences.ticketed")}
              </Button>
              <Button
                type="text"
                className={`${styles.tabButton} ${activeTab === "rentals" ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => onTabChange("rentals")}
              >
                {t("experiences.rentals")}
              </Button>
            </>
          )}
          <span style={{ position: "relative" }}>
            <button
              type="button"
              onClick={onFilterIconClick}
              className={`${styles.filterButton} ${isFilterDropdownOpen ? styles.filterButtonActive : ""}`}
              aria-label="Filter options"
              aria-expanded={isFilterDropdownOpen}
              aria-haspopup="true"
            >
              <FilterIcon height={60} width={60} />
            </button>
            {isFilterDropdownOpen && !isMobile && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000 }}>
                <FilterDropdown onClose={onCloseFilterDropdown} />
              </div>
            )}
            {isMobile && (
              <Drawer
                placement="bottom"
                open={isFilterDropdownOpen}
                setOpen={onCloseFilterDropdown}
                closable={false}
                title="Filter options"
                className={styles.mobileFilterDrawer}
                extra={<CloseIcon width={20} height={20} onClick={onCloseFilterDropdown} className="cursor-pointer" />}
              >
                <FilterDropdown onClose={onCloseFilterDropdown} />
              </Drawer>
            )}
          </span>
        </Flex>
        <Flex align="center" wrap gap={3} className={styles.statusFilterTabs}>
          {isCompactDevice ? (
            <Select
              value={activeListingTab}
              options={listingStatusOptions}
              onChange={onListingTabChange}
              className={styles.mobileStatusDropdown}
            />
          ) : (
            <>
              <Button
                type="text"
                className={`${styles.tab} ${activeListingTab === "published" ? styles.tabActive : styles.tabInactive}`}
                onClick={() => onListingTabChange("published")}
              >
                {t("experiences.published")} <span>{publishedCount}</span>
              </Button>
              <Button
                type="text"
                className={`${styles.tab} ${activeListingTab === "draft" ? styles.tabActive : styles.tabInactive}`}
                onClick={() => onListingTabChange("draft")}
              >
                {t("experiences.drafts")} <span>{draftCount}</span>
              </Button>
              <Button
                type="text"
                className={`${styles.tab} ${activeListingTab === "unpublished" ? styles.tabActive : styles.tabInactive}`}
                onClick={() => onListingTabChange("unpublished")}
              >
                {t("experiences.deactivated")} <span>{unpublishedCount}</span>
              </Button>
            </>
          )}
          {!isCompactDevice && (
            <Flex justify="center" align="center">
              <Flex
                justify="center"
                align="center"
                onClick={() => onListingChange("table")}
                className={`${selectedListing === "table" ? styles.listingTabActive : styles.listingTabInactive}`}
              >
                <ExperienceTableIcon color={selectedListing === "table" ? "#ffffff" : "#A8A9AA"} />
              </Flex>
              <Flex
                justify="center"
                align="center"
                onClick={() => onListingChange("listing")}
                className={`${selectedListing === "listing" ? styles.listingTabActive : styles.listingTabInactive}`}
              >
                <ExperienceListingIcon color={selectedListing === "listing" ? "#ffffff" : "#A8A9AA"} />
              </Flex>
            </Flex>
          )}
        </Flex>
      </>
    )
  }
)

FilterButtons.displayName = "FilterButtons"

FilterButtons.propTypes = {
  selectedListing: PropTypes.string,
  setSelectedListing: PropTypes.func,
  t: PropTypes.func,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  activeListingTab: PropTypes.string,
  onListingTabChange: PropTypes.func,
  publishedCount: PropTypes.number,
  draftCount: PropTypes.number,
  unpublishedCount: PropTypes.number,
  isFilterDropdownOpen: PropTypes.bool,
  onFilterIconClick: PropTypes.func,
  onCloseFilterDropdown: PropTypes.func,
  FilterDropdown: PropTypes.elementType
}

export default FilterButtons
